/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/jsx-no-constructed-context-values */
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import { useToast, UseToastOptions } from "@chakra-ui/react";
import {
  AppError,
  ErrorHandlerOptions,
  ErrorSeverity,
} from "@/types/errors";

interface ErrorContextType {
  errors: AppError[];
  addError: (error: AppError, options?: ErrorHandlerOptions) => void;
  clearError: (index: number) => void;
  clearAllErrors: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

interface ErrorProviderProps {
  children: ReactNode;
}

/**
 * Helper functions
 */

function getToastTitle(severity: ErrorSeverity): string {
  switch (severity) {
    case ErrorSeverity.INFO:
      return "Informácia";
    case ErrorSeverity.WARNING:
      return "Upozornenie";
    case ErrorSeverity.ERROR:
      return "Chyba";
    case ErrorSeverity.CRITICAL:
      return "Kritická chyba";
    default:
      return "Chyba";
  }
}

function getToastStatus(
  severity: ErrorSeverity,
): "info" | "warning" | "error" | "success" {
  switch (severity) {
    case ErrorSeverity.INFO:
      return "info";
    case ErrorSeverity.WARNING:
      return "warning";
    case ErrorSeverity.ERROR:
    case ErrorSeverity.CRITICAL:
      return "error";
    default:
      return "error";
  }
}

function getToastDuration(severity: ErrorSeverity): number {
  switch (severity) {
    case ErrorSeverity.INFO:
      return 3000; // 3 seconds
    case ErrorSeverity.WARNING:
      return 5000; // 5 seconds
    case ErrorSeverity.ERROR:
      return 7000; // 7 seconds
    case ErrorSeverity.CRITICAL:
      return 10000; // 10 seconds
    default:
      return 5000;
  }
}

/**
 * Error Provider for centralized error state management
 * Provides global error handling and toast notifications
 */
export function ErrorProvider({ children }: ErrorProviderProps) {
  const [errors, setErrors] = useState<AppError[]>([]);
  const toast = useToast();

  const addError = useCallback(
    (error: AppError, options?: ErrorHandlerOptions) => {
      // Add error to state
      setErrors((prev) => [...prev, error]);

      // Log to Sentry if enabled and in production
      if (
        options?.logToSentry !== false &&
        process.env.NODE_ENV === "production" &&
        typeof window !== "undefined"
      ) {
        // @ts-ignore - Sentry will be available after installation
        if (window.Sentry) {
          // @ts-ignore
          window.Sentry.captureException(error.originalError || error, {
            level: error.severity,
            extra: {
              type: error.type,
              message: error.message,
              timestamp: error.timestamp,
              context: options?.context || error.context,
            },
          });
        }
      }

      // Log to console in development
      if (process.env.NODE_ENV === "development") {
        console.error("Error caught by ErrorProvider:", {
          type: error.type,
          severity: error.severity,
          message: error.message,
          originalError: error.originalError,
          context: options?.context || error.context,
        });
      }

      // Show toast notification if not silent
      if (!options?.silent) {
        const toastOptions: UseToastOptions = {
          title: getToastTitle(error.severity),
          description: error.message,
          status: getToastStatus(error.severity),
          duration: getToastDuration(error.severity),
          isClosable: true,
          position: "top-right",
        };

        toast(toastOptions);
      }
    },
    [toast],
  );

  const clearError = useCallback((index: number) => {
    setErrors((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  // Auto-cleanup old errors (older than 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      setErrors((prev) =>
        prev.filter((error) => {
          const age = now.getTime() - error.timestamp.getTime();
          return age < 5 * 60 * 1000; // 5 minutes
        }),
      );
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  const value: ErrorContextType = {
    errors,
    addError,
    clearError,
    clearAllErrors,
  };

  return (
    <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>
  );
}

/**
 * Hook to access error context
 */
export function useErrorContext() {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error("useErrorContext must be used within ErrorProvider");
  }
  return context;
}

